/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jan 16 2024
 **/

import styled from "@emotion/styled";

import { ChatRenderer } from "@/frameworks/core/components";
import { usePersistUser } from "@/frameworks/core/persistence";
import { Button, Column, Row, Typography, TypographyCSS } from "@/frameworks/designStuff/components";
import { _P, _T } from "@rfice/language";
import { useProperties } from "@/frameworks/libraries";
import { useFileExtensions } from "@/frameworks/core/utils";

type ReplyTarget = {
	messageID: string;
	files?: ChatServices.Server.MessageSent.Data["files"] | undefined;
	roomID: string;
	message: ChatServices.Format2.Message;
	user: { name: string };
};

export default function ReplyTarget({
	replyTarget,
	roomID,
	onCancel
}: {
	replyTarget?: ReplyTarget;
	roomID: string;
	onCancel: () => void;
}) {
	const me = usePersistUser("me");
	const properties = useProperties();
	const { getFileExtension } = useFileExtensions();

	const scrollToTarget = () => {
		if (!replyTarget?.messageID) return;
		const massageElement = document.getElementById(replyTarget?.messageID);
		if (massageElement) massageElement.scrollIntoView({ behavior: "smooth", block: "start" });
	};
	const parsedFileData = replyTarget?.files?.map((item) => {
		return JSON.parse(item.data);
	});

	const images = parsedFileData?.filter((file) => file.type === "image");
	const hasImages = images && images?.length > 0;

	const files = parsedFileData?.filter((file) => file.type === "file");
	const videos = files?.filter((file) => {
		return getFileExtension(file.mime === "video/quicktime" ? "video/mp4" : file.mime) === "video";
	});
	const hasVideos = videos && videos.length > 0;

	const mediaLabel = (() => {
		const imageCount = images?.length ?? 0;
		const videoCount = videos?.length ?? 0;

		if (imageCount > 0 && videoCount > 0) {
			return _T("동영상 {{videoCount}}개, 사진 {{imageCount}}개", {
				videoCount,
				imageCount
			});
		}

		if (videoCount > 0) {
			return videoCount === 1 ? _T("동영상") : _T("동영상 {{count}}개", { count: videoCount });
		}

		if (imageCount > 0) {
			return imageCount === 1 ? _T("사진") : _T("사진 {{count}}개", { count: imageCount });
		}
	})();

	return (
		<>
			{replyTarget && replyTarget?.roomID === roomID && (
				<StyledWrapper gap={4}>
					<Row css={{ width: "100%" }} horizonAlign="distribute">
						<Row gap={8} verticalAlign="center">
							{hasVideos ? (
								<ReplayVideo
									imageUrl={`${properties.default.webDomain}${videos?.[0]?.videoThumbnailPath}?h=360`}
								/>
							) : hasImages ? (
								<ReplayImage imageUrl={`${images?.[0]?.url}?h=360`} />
							) : null}

							<Column gap={4}>
								<ReplyTo>
									{me.name === replyTarget.user.name
										? _T("나에게 답장")
										: _P("{{user}} 님에게 답장", {
												user: `<span id='target'>${replyTarget.user.name}</span>`
											})}
								</ReplyTo>
								<div>
									<ReplyWrapper onClick={() => scrollToTarget()}>
										<ChatRenderer chatForm={replyTarget.message} />
									</ReplyWrapper>
									<Typography typoSize="Body3" typoColor="neutral_50_100">
										{mediaLabel}
									</Typography>
								</div>
							</Column>
						</Row>
						<Button.Icon
							name="Close24"
							size="small"
							fill="neutral_00_100"
							filled="error"
							shape="round"
							type="button"
							onClick={onCancel}
						/>
					</Row>
				</StyledWrapper>
			)}
		</>
	);
}

const StyledWrapper = styled(Column)`
	border-radius: 12px;
	width: 100%;
	padding: 8px 16px;
	background-color: ${({ theme }) => theme.colors.deco8_10_100};
`;

const ReplyTo = styled(Typography)`
	#target {
		${TypographyCSS("Title2", "accent_40_100")}
	}
`;

const ReplyWrapper = styled(Row)`
	text-overflow: ellipsis;
	overflow: hidden;
	word-break: break-word;
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	cursor: pointer;

	// codeBlock은  다른 랜더 컴포넌트와 다르게 padding 처리가 있어서 상위에서 말줄임시 깨져보이기 때문에 해당 코드 추가
	.codeBlock {
		text-overflow: ellipsis;
		overflow: hidden;
		word-break: break-word;
		display: -webkit-box;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
	}
`;

const ReplayImage = styled.div<{ imageUrl?: string }>`
	width: 48px;
	height: 48px;
	border-radius: 4px;
	background-image: ${({ imageUrl }) => `url(${imageUrl})`};
	background-size: contain;
	background-position: center;
	background-repeat: no-repeat;
	background-color: ${({ theme }) => theme.colors.secondary_80_12};
`;

const ReplayVideo = styled(ReplayImage)`
	position: relative;

	&::after {
		content: "";
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 0;
		height: 0;
		border-left: 10px solid white;
		border-top: 6px solid transparent;
		border-bottom: 6px solid transparent;
		opacity: 0.9;
		pointer-events: none;
	}
`;
